/**
 * Single source of truth for API error handling.
 *
 * Every in-scope API failure uses one envelope:
 *   { success: false, message, error_code, errors?, data? }
 *
 * Rules:
 *  - HTTP status drives transport/auth behaviour (401, 403, 429, 5xx).
 *  - `error_code` drives product/business behaviour. Never branch on `message`.
 *  - `message` is the human-readable text for toasts/banners.
 *  - `errors` carries field-level validation errors, `data` any structured extras.
 *
 * `parseApiError` never throws and always returns an `AppApiError`, including for
 * network failures, cancellations, HTML/plain-text bodies and malformed JSON.
 */

export type ApiErrorEnvelope = {
  success: false;
  message: string;
  error_code: string;
  errors?: Record<string, string[]>;
  data?: Record<string, unknown>;
};

export function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return v.success === false && typeof v.message === 'string' && typeof v.error_code === 'string';
}

/**
 * Synthetic codes generated by the client (never sent by the backend).
 * Prefixed with `CLIENT_` so they cannot collide with server `error_code`s.
 */
export const CLIENT_ERROR_CODES = {
  NETWORK: 'CLIENT_NETWORK_ERROR',
  TIMEOUT: 'CLIENT_TIMEOUT',
  CANCELED: 'CLIENT_REQUEST_CANCELED',
  UNEXPECTED_RESPONSE: 'CLIENT_UNEXPECTED_RESPONSE',
  UNKNOWN: 'CLIENT_UNKNOWN_ERROR',
} as const;

export const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export type AppApiError = {
  status?: number;
  message: string;
  /** Backend code, or a `CLIENT_*` code for failures that never reached the API contract. */
  error_code?: string;
  errors?: Record<string, string[]>;
  data?: Record<string, unknown>;
  /** True when the failure matched the backend error envelope. */
  fromApi: boolean;
  originalError?: unknown;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** Axios puts a string body in `data` for non-JSON responses; try to recover JSON from it. */
function coerceBody(body: unknown): unknown {
  if (typeof body !== 'string') return body;
  const trimmed = body.trim();
  if (!trimmed.startsWith('{')) return undefined;
  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

function sanitizeFieldErrors(errors: unknown): Record<string, string[]> | undefined {
  if (!isRecord(errors)) return undefined;
  const out: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(errors)) {
    if (Array.isArray(value)) {
      const msgs = value.filter((m): m is string => typeof m === 'string');
      if (msgs.length) out[key] = msgs;
    } else if (typeof value === 'string') {
      out[key] = [value];
    }
  }
  return Object.keys(out).length ? out : undefined;
}

function clientError(
  error_code: string,
  message: string,
  extra: Partial<AppApiError> = {},
): AppApiError {
  return { message, error_code, fromApi: false, ...extra };
}

function isAppApiError(error: unknown): error is AppApiError {
  return (
    isRecord(error) &&
    typeof error.message === 'string' &&
    typeof error.fromApi === 'boolean' &&
    !('isAxiosError' in error)
  );
}

export function parseApiError(error: unknown): AppApiError {
  try {
    if (isAppApiError(error)) return error;

    const resp = isRecord(error) ? error.response : undefined;
    const status =
      isRecord(resp) && typeof resp.status === 'number' ? (resp.status as number) : undefined;

    if (status !== undefined) {
      const body = coerceBody((resp as Record<string, unknown>).data);
      if (isApiErrorEnvelope(body)) {
        return {
          status,
          message: body.message,
          error_code: body.error_code,
          errors: sanitizeFieldErrors(body.errors),
          data: isRecord(body.data) ? body.data : undefined,
          fromApi: true,
          originalError: error,
        };
      }
      // HTTP failure with HTML / plain text / legacy / malformed body.
      return clientError(CLIENT_ERROR_CODES.UNEXPECTED_RESPONSE, DEFAULT_ERROR_MESSAGE, {
        status,
        originalError: error,
      });
    }

    const code = isRecord(error) ? error.code : undefined;
    if (code === 'ERR_CANCELED') {
      return clientError(CLIENT_ERROR_CODES.CANCELED, 'The request was cancelled.', {
        originalError: error,
      });
    }
    if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
      return clientError(CLIENT_ERROR_CODES.TIMEOUT, 'The request timed out. Please try again.', {
        originalError: error,
      });
    }
    if (code === 'ERR_NETWORK' || (isRecord(error) && error.isAxiosError === true)) {
      return clientError(
        CLIENT_ERROR_CODES.NETWORK,
        'Unable to reach the server. Please check your internet connection.',
        { originalError: error },
      );
    }

    // Locally thrown errors (validation, missing data, ...) carry app-authored messages.
    if (error instanceof Error && error.message) {
      return clientError(CLIENT_ERROR_CODES.UNKNOWN, error.message, { originalError: error });
    }
  } catch {
    // fall through to the generic error
  }
  return clientError(CLIENT_ERROR_CODES.UNKNOWN, DEFAULT_ERROR_MESSAGE, { originalError: error });
}

export const VALIDATION_ERROR_CODE = 'VALIDATION_ERROR';

/**
 * Flattened `errors` messages (non-field first), de-duplicated and joined for display.
 * Field messages are prefixed with the sentence-case field name, e.g. `Name: Ensure this field has no more than 255 characters.`
 */
const toSentenceCase = (field: string): string => {
  const spaced = field.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const joinValidationErrors = (errors: Record<string, string[]>): string => {
  const { non_field_errors, ...fields } = errors;
  const fieldMessages = Object.entries(fields).flatMap(([field, msgs]) =>
    msgs.map((m) => `${toSentenceCase(field)}: ${m}`),
  );
  const all = [...(non_field_errors ?? []), ...fieldMessages];
  return [...new Set(all)].join(' ');
};

/**
 * Human-readable message for toasts/banners.
 * For `VALIDATION_ERROR`, uses the specific messages from `errors` (e.g. "You already have a
 * media item with this title.") instead of the generic API `message`.
 * Otherwise uses the API `message` when present; `fallback` replaces unexpected (non-envelope) HTTP failures.
 */
export const extractApiError = (error: unknown, fallback = DEFAULT_ERROR_MESSAGE): string => {
  if (!error) return fallback;
  const parsed = parseApiError(error);
  if (parsed.error_code === VALIDATION_ERROR_CODE && parsed.errors) {
    const detail = joinValidationErrors(parsed.errors);
    if (detail) return detail;
  }
  if (parsed.error_code === CLIENT_ERROR_CODES.UNEXPECTED_RESPONSE) return fallback;
  if (parsed.error_code === CLIENT_ERROR_CODES.UNKNOWN && !(error instanceof Error))
    return fallback;
  return parsed.message || fallback;
};

/** True if the parsed error carries the given backend `error_code`. */
export const hasErrorCode = (error: unknown, code: string): boolean =>
  parseApiError(error).error_code === code;

export const isAuthenticationError = (error: unknown): boolean =>
  parseApiError(error).status === 401;

export const isPermissionDeniedError = (error: unknown): boolean =>
  parseApiError(error).status === 403;

/** Seconds to wait before retrying (429 THROTTLED, OTP_RATE_LIMITED), when the API supplies it. */
export const getRetryAfter = (error: unknown): number | undefined => {
  const raw = parseApiError(error).data?.retry_after;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : undefined;
};

/** Form-level (non-field) validation message from `errors.non_field_errors`. */
export const getNonFieldError = (error: unknown): string | undefined =>
  parseApiError(error).errors?.non_field_errors?.join(' ') || undefined;

/**
 * Field error mapping from API response
 * Maps API field names to form field names
 */
export interface FieldErrors {
  [fieldName: string]: string;
}

/**
 * Field name mappings from API to form fields
 * Handles cases where API field names differ from form field names
 */
const API_TO_FORM_FIELD_MAP: Record<string, string> = {
  title: 'title',
  description: 'description',
  price: 'price',
  images: 'images',
  work_example_links: 'workExampleLinks',
  work_example_link: 'workExampleLinks',
  user_types: 'userTypeIds',
  tags: 'tagIds',
  alternate_email: 'alternateEmail',
  // Add more mappings as needed
};

/**
 * Extract field-specific errors from an API error.
 *
 * Maps `errors[field]` to form field names; multiple messages are joined.
 * `non_field_errors` is excluded — use `getNonFieldError` for the form-level message.
 * Example: { errors: { description: ["This field may not be blank."] } }
 *       -> { description: "This field may not be blank." }
 */
export const extractFieldErrors = (error: unknown): FieldErrors => {
  const fieldErrors: FieldErrors = {};
  if (!error) return fieldErrors;
  const errors = parseApiError(error).errors;
  if (!errors) return fieldErrors;
  for (const [apiField, messages] of Object.entries(errors)) {
    if (apiField === 'non_field_errors') continue;
    fieldErrors[API_TO_FORM_FIELD_MAP[apiField] || apiField] = messages.join(' ');
  }
  return fieldErrors;
};

/**
 * Check if an API error has field-specific errors
 */
export const hasFieldErrors = (error: unknown): boolean =>
  Object.keys(extractFieldErrors(error)).length > 0;
